import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { BuildingLibraryIcon, UsersIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { scopedRosterRole, useAllManageableGroups } from '@/hooks/useManageableGroups';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import PausedChip from '@/components/lock/PausedChip';
import RosterGroupListItem, { type RosterSidebarGroup } from './RosterGroupListItem';

const COLLAPSE_STORAGE_KEY = 'flock_roster_sidebar_collapsed';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

export interface RosterSidebarOrg {
  orgId: string;
  orgName: string;
}

interface OrgNode {
  orgId: string;
  orgName: string;
  /** Org has a role that can open the org-level ("All Members") roster. */
  hasOrgRoster: boolean;
  groups: RosterSidebarGroup[];
}

interface Props {
  /** `org:<orgId>` for an org-level entry, or a groupId. */
  selectedKey: string | null;
  onSelectOrg: (org: RosterSidebarOrg) => void;
  onSelectGroup: (group: RosterSidebarGroup) => void;
}

/**
 * Cross-org roster sidebar: Organization (collapsible) → optional pinned
 * "All Members" entry (leadership scope only) → community groups. Groups are
 * the union of the user's memberships and the groups their role lets them
 * shepherd (org_admin: all org groups; group_leader: led groups), so every
 * user sees exactly what they're scoped to.
 */
export default function RosterSidebar({ selectedKey, onSelectOrg, onSelectGroup }: Props) {
  const { organizations } = useAuth();
  const { byOrg: manageableByOrg } = useAllManageableGroups();
  const { isOrgLocked } = useOrgLockState();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const tree = useMemo((): OrgNode[] => {
    const q = search.trim().toLowerCase();
    const orgs: OrgNode[] = [];
    for (const membership of organizations) {
      const orgName = membership.organization.name;
      const orgId = membership.organization_id;
      const hasOrgRoster = scopedRosterRole(membership) !== null;

      // Memberships ∪ role-scoped groups, deduped by id
      const merged = new Map<string, RosterSidebarGroup>();
      for (const g of membership.groups ?? []) {
        merged.set(g.id, {
          id: g.id,
          name: g.name,
          orgId,
          orgName,
          imageUrl: g.group_image_url,
        });
      }
      for (const g of manageableByOrg[orgId] ?? []) {
        if (!merged.has(g.id)) {
          merged.set(g.id, {
            id: g.id,
            name: g.name,
            orgId,
            orgName,
            imageUrl: g.group_image_url,
          });
        }
      }

      const groups = Array.from(merged.values())
        .filter(
          (g) =>
            !q || g.name.toLowerCase().includes(q) || orgName.toLowerCase().includes(q)
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      if (groups.length === 0 && !hasOrgRoster) continue;
      if (q && groups.length === 0 && !orgName.toLowerCase().includes(q)) continue;
      orgs.push({ orgId, orgName, hasOrgRoster, groups });
    }
    orgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
    return orgs;
  }, [organizations, manageableByOrg, search]);

  const searching = search.trim().length > 0;
  const hasAnyEntries = organizations.some(
    (o) => (o.groups?.length ?? 0) > 0 || scopedRosterRole(o) !== null
  );

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-flock-gray-200 bg-white">
      <div className="border-b border-flock-gray-200 p-3">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups"
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {!hasAnyEntries && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            You're not in any groups yet.
          </div>
        )}

        {hasAnyEntries && searching && tree.length === 0 && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            No groups match your search.
          </div>
        )}

        {tree.map((org) => {
          const orgKey = `org:${org.orgId}`;
          // While searching, force everything open so matches are visible
          const orgCollapsed = !searching && collapsed.has(orgKey);
          return (
            <div key={org.orgId} className="mb-1">
              <button
                onClick={() => toggleCollapsed(orgKey)}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left hover:bg-flock-navy-50"
              >
                {orgCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                )}
                <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-flock-navy-500" />
                <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-flock-navy-700">
                  {org.orgName}
                </span>
                {isOrgLocked(org.orgId) && <PausedChip />}
              </button>

              {!orgCollapsed && (
                <div className="space-y-0.5">
                  {org.hasOrgRoster && (
                    <button
                      onClick={() => onSelectOrg({ orgId: org.orgId, orgName: org.orgName })}
                      className={clsx(
                        'flex w-full items-center gap-3 rounded-lg py-2 pl-6 pr-3 text-left transition',
                        selectedKey === orgKey ? 'bg-flock-navy-100' : 'hover:bg-flock-navy-50'
                      )}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-flock-navy-600 text-white">
                        <UsersIcon className="h-5 w-5" />
                      </div>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-flock-navy-900">
                        All Members
                      </span>
                    </button>
                  )}
                  {org.groups.map((group) => (
                    <RosterGroupListItem
                      key={group.id}
                      group={group}
                      selected={selectedKey === group.id}
                      onSelect={onSelectGroup}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
